import React from 'react'
import { Controller } from 'react-hook-form'
import { UserRpps } from '@saas/interfaces/core'
import { AttendingPhysicianIds, AttendingPhysicianStateIds } from '@toktokdoctech/tokapi-client'
import { searchPhysicians } from '../../../../utils/physicians'
import { formatPhysician } from '../../../../utils/requiredExpert'
import LargeSwitch from '../../../Common/largeSwitch/LargeSwitch'
import SearchRpps from '../../../Common/searchRpps/SearchRpps'

import PatientFormField from '../PatientFormField'
import { shouldDirty } from '../PatientIdentity/_utils/_utils'
import { useFormContextPatient } from '../_utils'
import FlexBox from '../../../Common/flexBox/FlexBox'

// The form holds the médecin traitant as a PossiblyAttendingPhysician:
//   Unknown                            => not specified yet
//   None                               => "Aucun médecin traitant"
//   Known with { rpps, fullName }      => a physician
const AttendingPhysician = () => {
    const [formatedRequiredExpert, setFormatedRequiredExpert] = React.useState<UserRpps | undefined>()
    const [isFetchingRequiredExpert, setIsFetchingRequiredExpert] = React.useState(false)
    const { control, getValues, setValue } = useFormContextPatient()
    const token = localStorage.getItem('sessionToken')
    const { attendingPhysician } = getValues()

    const knownRpps =
        attendingPhysician?.state === AttendingPhysicianStateIds.Known ? attendingPhysician.physician?.rpps : undefined

    React.useEffect(() => {
        if (!knownRpps || !token) return
        setIsFetchingRequiredExpert(true)
        searchPhysicians(knownRpps, token)
            .then((result) => {
                if (result && result.physicians.length > 0) setFormatedRequiredExpert(result.physicians[0])
            })
            .catch((err) => console.log(err))
            .finally(() => setIsFetchingRequiredExpert(false))
    }, [knownRpps, token])

    const setRequiredExpert = (value: AttendingPhysicianIds) =>
        setValue('attendingPhysician', value, shouldDirty)

    const handleSelectPhysician = (physician: UserRpps) => {
        setRequiredExpert({
            state: AttendingPhysicianStateIds.Known,
            physician: {
                rpps: String(physician.identifiantpp),
                fullName: formatPhysician(physician),
                label: formatPhysician(physician),
            },
        })
        setFormatedRequiredExpert(physician)
    }

    // Search cleared while still looking for a physician => Unknown.
    const handleClearPhysician = () => {
        setRequiredExpert({ state: AttendingPhysicianStateIds.Unknown })
        setFormatedRequiredExpert(undefined)
    }

    // "Aucun médecin traitant": on => None, off => Unknown.
    const handleToggleNoAttendingPhysician = (checked: boolean) => {
        setRequiredExpert(checked ? { state: AttendingPhysicianStateIds.None } : { state: AttendingPhysicianStateIds.Unknown })
        setFormatedRequiredExpert(undefined)
    }

    return (
        <Controller
            control={control}
            name="attendingPhysician"
            render={({ field, fieldState: { error } }) => {
                const { value } = field
                const isKnownNone = value?.state === AttendingPhysicianStateIds.None
                return (
                    <PatientFormField
                        variant="custom"
                        label="Médecin traitant du patient"
                        name={field.name}
                        status={{
                            error,
                            // All three states are acceptable/savable, including Unknown
                            // ("Aucun médecin traitant" off + empty search), so the field is never invalid.
                            valid: Boolean(value),
                        }}
                        size="0.5"
                        isLoading={isFetchingRequiredExpert}
                        dataCyScope="PatientField"
                        render={() => (
                            <FlexBox flexDirection="column">
                                {!isKnownNone && (
                                    <SearchRpps
                                        onRppsUserChange={(userRpps) => {
                                            if (userRpps && typeof userRpps !== 'string') {
                                                handleSelectPhysician(userRpps)
                                            }
                                        }}
                                        selectedValue={formatedRequiredExpert}
                                        codeprofession={10}
                                        onClear={handleClearPhysician}
                                    />
                                )}
                                <LargeSwitch
                                    label="Aucun médecin traitant"
                                    onChange={(e) => handleToggleNoAttendingPhysician(e.target.checked)}
                                    defaultChecked={isKnownNone}
                                />
                            </FlexBox>
                        )}
                    />
                )
            }}
        />
    )
}

export default AttendingPhysician
